<template>
    <div class="x-markdown-viewer-wrapper">
        <div class="x-markdown-viewer" v-html="renderedContent"></div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { formatMarkdown } from '@/utils/markdown';

export default defineComponent({
    name: 'XMarkdownViewer',
    props: {
        // Markdown 文本内容
        content: {
            type: String,
            default: ''
        }
    },
    computed: {
        renderedContent(): string {
            return formatMarkdown(this.content);
        }
    }
});
</script>

<style>
/* 全局样式，不使用 scoped，以便可以影响内部生成的 HTML */

/* 外层包装：宽表格时出现水平滚动条，避免内容溢出 */
.x-markdown-viewer-wrapper {
    overflow-x: auto;
    max-width: 100%;
}

.x-markdown-viewer {
    line-height: 1.6;
    color: inherit;
    /* min-width: min-content; 让内部宽内容（如表格）撑开，由 wrapper 提供滚动 */
}

/* 标题样式 */
.x-markdown-viewer h1,
.x-markdown-viewer h2,
.x-markdown-viewer h3,
.x-markdown-viewer h4,
.x-markdown-viewer h5,
.x-markdown-viewer h6 {
    margin-top: 1em;
    margin-bottom: 0.5em;
    font-weight: 600;
    line-height: 1.25;
}

.x-markdown-viewer h1 {
    font-size: 1.75em;
    border-bottom: 2px solid #e5e7eb;
    padding-bottom: 0.3em;
}

.x-markdown-viewer h2 {
    font-size: 1.5em;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 0.3em;
}

.x-markdown-viewer h3 {
    font-size: 1.25em;
}

.x-markdown-viewer h4 {
    font-size: 1.1em;
}

.x-markdown-viewer h5,
.x-markdown-viewer h6 {
    font-size: 1em;
}

/* 段落样式 */
.x-markdown-viewer p {
    margin-top: 0;
    margin-bottom: 1em;
}

/* 列表样式 */
.x-markdown-viewer ul,
.x-markdown-viewer ol {
    margin-top: 0;
    margin-bottom: 1em;
    padding-left: 2em;
}

.x-markdown-viewer li {
    margin-bottom: 0.25em;
}

.x-markdown-viewer ul ul,
.x-markdown-viewer ul ol,
.x-markdown-viewer ol ul,
.x-markdown-viewer ol ol {
    margin-top: 0.25em;
    margin-bottom: 0.25em;
}

/* 代码块样式 */
.x-markdown-viewer pre {
    background-color: #f6f8fa;
    border-radius: 6px;
    padding: 16px;
    overflow: auto;
    margin: 1em 0;
    font-size: 0.875em;
    line-height: 1.45;
}

.x-markdown-viewer pre code {
    background-color: transparent;
    padding: 0;
    border-radius: 0;
    font-size: inherit;
    color: inherit;
}

/* 行内代码样式 */
.x-markdown-viewer code {
    background-color: rgba(175, 184, 193, 0.2);
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-family: 'SFMono-Regular', 'Consolas', 'Liberation Mono', 'Menlo', 'Courier', monospace;
    font-size: 0.875em;
}

/* 引用块样式 */
.x-markdown-viewer blockquote {
    margin: 1em 0;
    padding: 0 1em;
    color: #6b7280;
    border-left: 4px solid #d1d5db;
}

.x-markdown-viewer blockquote > :first-child {
    margin-top: 0;
}

.x-markdown-viewer blockquote > :last-child {
    margin-bottom: 0;
}

/* 链接样式 */
.x-markdown-viewer a {
    color: #3b82f6;
    text-decoration: none;
}

.x-markdown-viewer a:hover {
    text-decoration: underline;
}

/* 表格样式（宽表由 .x-markdown-viewer-wrapper 提供水平滚动） */
.x-markdown-viewer table {
    border-collapse: collapse;
    width: 100%;
    /* min-width: max-content; 列多时保持表格完整宽度，由外层滚动 */
    margin: 1em 0;
}

.x-markdown-viewer table th,
.x-markdown-viewer table td {
    padding: 6px 13px;
    border: 1px solid #d1d5db;
}

.x-markdown-viewer table th {
    background-color: #f3f4f6;
    font-weight: 600;
}

.x-markdown-viewer table tr:nth-child(even) {
    background-color: #f9fafb;
}

/* 分隔线样式 */
.x-markdown-viewer hr {
    height: 2px;
    padding: 0;
    margin: 1.5em 0;
    background-color: #e5e7eb;
    border: 0;
}

/* 图片样式 */
.x-markdown-viewer img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 0.5em 0;
}

/* 强调文本样式 */
.x-markdown-viewer strong {
    font-weight: 600;
}

.x-markdown-viewer em {
    font-style: italic;
}

/* 删除线样式 */
.x-markdown-viewer del {
    text-decoration: line-through;
}

/* 任务列表样式 */
.x-markdown-viewer input[type="checkbox"] {
    margin-right: 0.5em;
}

/* 用户消息（蓝色背景）中的样式调整 */
.user-message .x-markdown-viewer pre {
    background-color: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.user-message .x-markdown-viewer code {
    background-color: rgba(255, 255, 255, 0.2);
    color: inherit;
}

.user-message .x-markdown-viewer a {
    color: #ffffff;
    text-decoration: underline;
}

.user-message .x-markdown-viewer h1,
.user-message .x-markdown-viewer h2 {
    border-bottom-color: rgba(255, 255, 255, 0.3);
}

.user-message .x-markdown-viewer blockquote {
    border-left-color: rgba(255, 255, 255, 0.5);
    color: rgba(255, 255, 255, 0.9);
}

.user-message .x-markdown-viewer table th,
.user-message .x-markdown-viewer table td {
    border-color: rgba(255, 255, 255, 0.3);
}

.user-message .x-markdown-viewer table th {
    background-color: rgba(255, 255, 255, 0.1);
}

.user-message .x-markdown-viewer table tr:nth-child(even) {
    background-color: rgba(255, 255, 255, 0.05);
}

.user-message .x-markdown-viewer hr {
    background-color: rgba(255, 255, 255, 0.3);
}

/* 响应式调整 */
@media (max-width: 640px) {
    .x-markdown-viewer {
        font-size: 0.9em;
    }
    
    .x-markdown-viewer pre {
        padding: 12px;
    }
}
</style>
